import { Common, CustomScrollbar, Icon } from "@/components";
import { useEffect } from "react";
import ReactTooltip from "react-tooltip";
import { TaxType, AddressMode } from "../models";
import clsx from "clsx";

interface Field {
  key: string;
  label: string;
  required: boolean;
  tooltip?: string;
}

interface MappingTableProps {
  fields: Field[];
  fileColumns: string[];
  mappingData: { [key: string]: string };
  onMapping: (key: string, value: string) => void;
  taxType?: TaxType;
  addressMode: AddressMode;
  onToggleAddressMode: () => void;
}

const MappingTable = ({
  fields,
  fileColumns,
  mappingData,
  onMapping,
  taxType,
  addressMode,
  onToggleAddressMode,
}: MappingTableProps) => {
  const getStatus = (field: Field) => {
    const isMatched = Boolean(mappingData[field.key]);

    if (field.required && !isMatched) {
      return (
        <div className="flex items-center text-red">
          <Icon.Close iconType="stroke" className="stroke-red mr-1" />
          Required
        </div>
      );
    }

    if (isMatched) {
      return (
        <div className="flex items-center text-green">
          <Icon.Checked className="fill-green mr-1" />
          Matched
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [addressMode]);

  return (
    <div className="h-[calc(100vh-296px)]">
      <CustomScrollbar>
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-10">
              <th className="px-4 py-3 text-left font-semibold text-hd5 text-neutral-40">
                Tom column
              </th>
              <th className="px-4 py-3 text-left font-semibold text-hd5 text-neutral-40 w-[720px]">
                Your data column
              </th>
              <th className="px-4 py-3 text-left font-semibold text-hd5 text-neutral-40 w-[240px]"></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <>
                {field.key === "addressLine1" && (
                  <tr key={"address"}>
                    <td colSpan={3} className="px-4 pt-3">
                      <div className="flex items-center h-9">
                        <div className="mr-2">
                          <Common.Button
                            className="hover:bg-primary-10 transition-colors"
                            color="blue"
                            variant="outline"
                            rounded={false}
                            size="xs"
                            iconLeft={Icon.MinusBorder}
                            onClick={onToggleAddressMode}
                            title={`Switch to Single Line Address`}
                            aria-label={`Switch to Single Line Address`}
                            tabIndex={0}
                          />
                        </div>
                        Address
                        {field.tooltip && (
                          <div
                            className="ml-2 text-neutral-50 cursor-help"
                            data-tip={field.tooltip}
                          >
                            <Icon.InfoFill className="fill-neutral-50" />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                <tr key={field.key}>
                  <td
                    className={clsx(
                      "relative px-4 pt-3",
                      (field.key === "addressLine1" ||
                        field.key === "addressLine2" ||
                        field.key === "suburb" ||
                        field.key === "state" ||
                        field.key === "postcode" ||
                        field.key === "country") &&
                        "pl-12"
                    )}
                  >
                    <div className="flex items-center">
                      {field.key === "address" && (
                        <div className="mr-2">
                          <Common.Button
                            className="hover:bg-primary-10 transition-colors"
                            color="blue"
                            variant="outline"
                            rounded={false}
                            size="xs"
                            iconLeft={Icon.PlusBorder}
                            onClick={onToggleAddressMode}
                            title={`Switch to Multi Line Address`}
                            aria-label={`Switch to Multi Line Address`}
                            tabIndex={0}
                          />
                        </div>
                      )}
                      {field.key === "addressLine1" && (
                        <div className="absolute h-[56px] left-[27px] -top-[7px] bottom-0 border border-line z-0"></div>
                      )}
                      {(field.key === "addressLine2" ||
                        field.key === "suburb" ||
                        field.key === "state" ||
                        field.key === "postcode" ||
                        field.key === "country") && (
                        <div className="absolute h-[49px] left-[27px] -top-[0] bottom-0 border border-line z-0"></div>
                      )}
                      {field.label}
                      {taxType && field.key === "price" && (
                        <span className="text-neutral-50 ml-1">
                          (
                          {taxType === TaxType.INCLUSIVE
                            ? "Tax Inclusive"
                            : "Tax Exclusive"}
                          )
                        </span>
                      )}
                      {field.required && (
                        <span className="text-red ml-1">*</span>
                      )}
                      {field.tooltip && field.key !== "addressLine1" && (
                        <div
                          className="ml-2 text-neutral-50 cursor-help"
                          data-tip={field.tooltip}
                        >
                          <Icon.InfoFill className="fill-neutral-50" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 pt-3 w-[720px]">
                    <div className="flex items-center space-x-2">
                      <Icon.Conversion
                        className="fill-neutral-50"
                        size={12}
                        taxType={taxType}
                      />
                      <div className="flex-1">
                        <Common.Selectbox
                          className="w-full"
                          options={[
                            { id: -1, text: "", value: "" },
                            ...fileColumns.map((col, index) => ({
                              id: index,
                              text: col,
                              value: col,
                            })),
                          ]}
                          value={mappingData[field.key] || ""}
                          onChange={(e) => {
                            onMapping(field.key, e.target.value);
                          }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 pt-3 w-[240px]">{getStatus(field)}</td>
                </tr>
              </>
            ))}
          </tbody>
        </table>
      </CustomScrollbar>
    </div>
  );
};

export default MappingTable;
